import { useState, useEffect, useCallback } from 'react';
import { producaoService } from '../../services';
import { imprimirComanda } from '../../utils/print';
import './Producao.css';

const FILTROS = [
  { value: 'fila',     label: 'Na Fila',  icon: '⏳', cor: '#1976d2' },
  { value: 'entregue', label: 'Entregue', icon: '🏁', cor: '#757575' },
];

const statusColors = { fila: 'badge-info', entregue: 'badge-success' };
const statusLabels = { fila: '⏳ Na Fila', entregue: '🏁 Entregue' };

// Data local no formato YYYY-MM-DD (toISOString usaria UTC e adiantaria o dia à noite)
const hoje = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function Producao() {
  const [todos, setTodos] = useState([]);
  const [aba, setAba] = useState('producao'); // 'producao' | 'impressao'
  const [filtro, setFiltro] = useState('fila');
  const [dataInicio, setDataInicio] = useState(hoje());
  const [dataFim, setDataFim] = useState(hoje());
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(() => {
    setLoading(true);
    producaoService.listar({ data_inicio: dataInicio, data_fim: dataFim })
      .then(res => setTodos(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dataInicio, dataFim]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleStatus = async (id, status) => {
    try {
      await producaoService.atualizarStatus(id, status);
      carregar();
    } catch {}
  };

  const contadores = FILTROS.reduce((acc, f) => {
    acc[f.value] = todos.filter(i => i.status === f.value).length;
    return acc;
  }, {});

  const itens = todos.filter(i => i.status === filtro);

  // Agrupa itens "na fila" por comanda/pedido para a aba de impressão
  const comandasFila = (() => {
    const fila = todos.filter(i => i.status === 'fila');
    const grupos = new Map();
    fila.forEach(i => {
      const chave = i.pedido_id || `p${i.pedido_numero}`;
      if (!grupos.has(chave)) {
        grupos.set(chave, {
          chave,
          pedido_numero: i.pedido_numero,
          pedido_tipo: i.pedido_tipo,
          mesa_numero: i.mesa_numero,
          comanda_numero: i.comanda_numero,
          cliente_nome: i.cliente_nome,
          itens: [],
        });
      }
      grupos.get(chave).itens.push(i);
    });
    return Array.from(grupos.values());
  })();

  const imprimirComandaFila = (g) => {
    const origem = !g.mesa_numero
      ? (g.pedido_tipo === 'balcao' ? 'Balcão' : g.pedido_tipo === 'delivery' ? 'Delivery' : null)
      : null;
    imprimirComanda({
      titulo: 'PRODUÇÃO',
      mesa: g.mesa_numero,
      comanda: g.comanda_numero,
      cliente: g.cliente_nome || null,
      origem,
      itens: g.itens.map(i => ({
        nome: i.produto_nome,
        quantidade: i.quantidade,
        observacao: i.observacao,
      })),
      mostrarPrecos: false,
      rodape: g.pedido_numero ? `Pedido #${g.pedido_numero}` : '',
    });
  };

  return (
    <div className="producao-page">
      {/* Abas */}
      <div className="producao-abas">
        <button className={`producao-aba ${aba === 'producao' ? 'ativo' : ''}`} onClick={() => setAba('producao')}>
          🍳 Produção
        </button>
        <button className={`producao-aba ${aba === 'impressao' ? 'ativo' : ''}`} onClick={() => setAba('impressao')}>
          🖨 Impressão
        </button>
      </div>

      {/* Filtro de data */}
      <div className="producao-datas">
        <label>De</label>
        <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        <label>Até</label>
        <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        <button className="btn btn-sm" style={{ background: '#1a1a2e', color: '#fff', border: 'none' }} onClick={carregar}>
          🔄 Atualizar
        </button>
      </div>

      {aba === 'producao' ? (
        <>
          {/* Botões de status com contador */}
          <div className="producao-filtros">
            {FILTROS.map(f => (
              <button
                key={f.value}
                className={`producao-filtro-btn ${filtro === f.value ? 'ativo' : ''}`}
                style={{ '--cor': f.cor }}
                onClick={() => setFiltro(f.value)}
              >
                <span className="pf-icon">{f.icon}</span>
                <span className="pf-label">{f.label}</span>
                <span className="pf-count">{contadores[f.value]}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading"><div className="spinner"></div>Carregando...</div>
          ) : (
            <div className="producao-grid">
              {itens.map(item => (
                <div key={item.id} className={`producao-card producao-${item.status}`}>
                  <div className="producao-header">
                    <span className="producao-pedido">Pedido #{item.pedido_numero}</span>
                    <span className={`badge ${statusColors[item.status]}`}>{statusLabels[item.status]}</span>
                  </div>
                  <div className="producao-produto">
                    <span className="producao-emoji">{item.emoji || '🥟'}</span>
                    <div>
                      <strong>{item.produto_nome}</strong>
                      <small>{item.quantidade}x • {item.pedido_tipo}</small>
                    </div>
                  </div>
                  {item.observacao && <div className="producao-obs">📝 {item.observacao}</div>}
                  <div className="producao-tempo">⏱️ {item.minutos_espera} min</div>
                  <div className="producao-acoes">
                    {item.status === 'fila' && <button className="btn btn-sm btn-success" onClick={() => handleStatus(item.id, 'entregue')}>✅ Entregue</button>}
                  </div>
                </div>
              ))}
              {itens.length === 0 && (
                <p className="no-data">Nenhum item com status "{FILTROS.find(f => f.value === filtro)?.label}" neste período</p>
              )}
            </div>
          )}
        </>
      ) : (
        // Aba Impressão: comandas com itens "Na Fila"
        loading ? (
          <div className="loading"><div className="spinner"></div>Carregando...</div>
        ) : (
          <div className="producao-grid">
            {comandasFila.map(g => (
              <div key={g.chave} className="producao-card producao-fila">
                <div className="producao-header">
                  <span className="producao-pedido">
                    {g.mesa_numero
                      ? `Mesa ${g.mesa_numero}`
                      : g.pedido_tipo === 'balcao'
                        ? 'Balcão'
                        : g.pedido_tipo === 'delivery'
                          ? 'Delivery'
                          : `Pedido #${g.pedido_numero}`}
                    {g.comanda_numero ? ` • Comanda #${g.comanda_numero}` : ''}
                  </span>
                  <span className="badge badge-info">{g.itens.length} item(ns)</span>
                </div>
                {g.cliente_nome && <div className="producao-obs">👤 {g.cliente_nome}</div>}
                <div className="producao-impressao-itens">
                  {g.itens.map(i => (
                    <div key={i.id} className="producao-impressao-item">
                      <span className="producao-emoji">{i.emoji || '🥟'}</span>
                      <span className="pii-qtd">{i.quantidade}x</span>
                      <span className="pii-nome">{i.produto_nome}</span>
                      {i.observacao && <span className="pii-obs">📝 {i.observacao}</span>}
                    </div>
                  ))}
                </div>
                <div className="producao-acoes">
                  <button className="btn btn-sm btn-primary" onClick={() => imprimirComandaFila(g)}>🖨 Imprimir comanda</button>
                </div>
              </div>
            ))}
            {comandasFila.length === 0 && (
              <p className="no-data">Nenhuma comanda com itens na fila neste período</p>
            )}
          </div>
        )
      )}
    </div>
  );
}
